import * as turf from "@turf/turf"

import { DisplayedImagePosition, FeatureWithDistance, ImageFeature } from "./ImageNavigator.types"

const MAX_DISTANCE_KM = 0.05

function angleDiff(a: number, b: number): number {
    const diff = Math.abs(((a - b) % 360 + 360) % 360)
    return Math.min(diff, 360 - diff)
}

function toTurfBearing(bearing: number): number {
    return bearing > 180 ? bearing - 360 : bearing
}

export function findAdjacentImage(
    imageCollection: { features?: unknown[] },
    displayedImagePosition: unknown,
    defaultBearing: number,
    roadBearing: number | null,
): ImageFeature | null {
    const position = displayedImagePosition as DisplayedImagePosition
    const features = (imageCollection.features ?? []) as ImageFeature[]
    if (!features.length) return null

    const currentPoint = turf.point([position.lng, position.lat])

    // Step 1 — keep only images within 50 m, sorted nearest-first.
    const nearby: FeatureWithDistance[] = features
        .map(f => ({
            feature: f,
            distance: turf.distance(currentPoint, turf.point(f.geometry.coordinates), { units: "kilometers" }),
        }))
        .filter(({ distance }) => distance <= MAX_DISTANCE_KM)
        .sort((a, b) => a.distance - b.distance)

    if (!nearby.length) return null

    // nearby[0] is the current image; everything after is a navigation candidate.
    const candidates = nearby.slice(1)
    if (!candidates.length) return null

    const currentTurfPoint = turf.point(nearby[0].feature.geometry.coordinates)

    // Step 2 — compute the bearing from the current image to each candidate.
    // turf.bearing returns [-180, 180]; normalise to [0, 360).
    const withBearing = candidates.map(({ feature, distance }) => ({
        feature,
        distance,
        bearing: (turf.bearing(currentTurfPoint, turf.point(feature.geometry.coordinates)) + 360) % 360,
    }))

    const searchBearing = roadBearing ?? defaultBearing

    // Step 4 — project a target point 3 m ahead and pick the nearest candidate.
    // toTurfBearing converts [0, 360) back to [-180, 180] as expected by turf.destination.
    const searchTarget = turf.destination(currentPoint, 0.001, toTurfBearing(searchBearing))

    const best = [...withBearing]
        .filter(({ bearing }) => angleDiff(bearing, searchBearing) <= 90)
        .sort((a, b) =>
            turf.distance(searchTarget, turf.point(a.feature.geometry.coordinates)) -
            turf.distance(searchTarget, turf.point(b.feature.geometry.coordinates)),
        )?.[0]

    return best?.feature ?? null
}

function bearingBetween(from: number[], to: number[]): number {
    return (turf.bearing(turf.point(from), turf.point(to)) + 360) % 360
}

// Walks `steps` images away from `startImage`, following the travel direction, and stops early when there is no further image.
export function findImageAfterSteps(
    imageCollection: { features?: unknown[] },
    startPosition: DisplayedImagePosition,
    startImage: ImageFeature,
    defaultBearing: number,
    steps: number,
): { image: ImageFeature, bearing: number } {
    let previousCoordinates = [startPosition.lng, startPosition.lat]
    let image = startImage
    let bearing = bearingBetween(previousCoordinates, image.geometry.coordinates)

    for (let step = 1; step < steps; step++) {
        const [lng, lat] = image.geometry.coordinates
        const adjacentImage = findAdjacentImage(imageCollection, { lng, lat }, defaultBearing, bearing)
        if (!adjacentImage) break
        previousCoordinates = image.geometry.coordinates
        image = adjacentImage
        bearing = bearingBetween(previousCoordinates, image.geometry.coordinates)
    }

    return { image, bearing }
}
